import { useState, useEffect, useMemo, useRef } from "preact/compat";
import initDropdown from "@components/FormControls/Dropdown";
import { useFormControl } from "@components/FormControls/useFormControl";
import { useAdvertApplicationMessages } from "./useAdvertApplicationMessages";
import type {
    AdvertApplicationMessagesProps,
    ApplicationAction,
    ApplicationStatus,
    MessageThread,
    TransformedMessage,
} from "./AdvertApplicationMessagesTypes";
import { formatDanishDateTime } from "@/utils/date";
import { label } from "@/utils/label";

type ThreadStatus = "unread" | "read" | "replied";

/** Mirrors the admin's thread status from the applicant's side: "replied" is
 *  you having had the last word, not PKA. */
function threadStatus(thread: MessageThread): ThreadStatus {
    if (thread.hasUnread) return "unread";
    const last = thread.messages[thread.messages.length - 1];
    if (!last) return "read";
    return last.isFromUser ? "replied" : "read";
}

function ThreadStatusIcon({ status }: { status: ThreadStatus }) {
    // The sprite has no double-check, so read and replied separate by colour.
    const variant =
        status === "unread"
            ? "aam-icon-clock text-warning-yellow"
            : status === "replied"
              ? "aam-icon-check text-correct-green"
              : "aam-icon-check text-medium-blue";
    return <span className={`aam-icon h-4 w-4 ${variant}`} aria-hidden="true" />;
}

type StatusTexts = {
    status: Partial<Record<ApplicationStatus, string>>;
    openHouse: string;
};

// Same idea as the admin's ApplicantStatusBadge: where the application is, coloured by how it's going.
const chipTone: Record<ApplicationStatus, string> = {
    Created: "bg-light-sand text-dark-text",
    Invited: "bg-light-blue text-dark-blue",
    ConfirmedOpenHouse: "bg-light-blue text-dark-blue",
    RejectedOpenHouse: "bg-light-gray text-dark-text/70",
    AwaitingInterest: "bg-warning-yellow/30 text-dark-text",
    Interested: "bg-light-green text-dark-green",
    Awarded: "bg-light-green text-dark-green",
    NotInterested: "bg-light-gray text-dark-text/70",
    Withdrawn: "bg-light-gray text-dark-text/70",
    Rejected: "bg-pka-red/10 text-bordeaux-red",
    Deleted: "bg-light-gray text-dark-text/70",
};

/** Status chip; the date it was reached (and the open house time once invited) shows on hover. */
function ApplicationStatusChip({ thread, texts }: { thread: MessageThread; texts: StatusTexts }) {
    const text = texts.status[thread.status];
    if (!text) return null;
    const showOpenHouse =
        (thread.status === "Invited" || thread.status === "ConfirmedOpenHouse") && thread.selectedOpenHouseStart;
    const detail = [
        formatDanishDateTime(thread.statusDate),
        showOpenHouse ? `${texts.openHouse} ${formatDanishDateTime(thread.selectedOpenHouseStart)}` : "",
    ]
        .filter(Boolean)
        .join(" · ");
    return (
        <span
            title={detail || undefined}
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${chipTone[thread.status] ?? chipTone.Created}`}
        >
            {text}
            {detail && <span className="sr-only">, {detail}</span>}
        </span>
    );
}

type ApplicationTexts = {
    actions: Record<ApplicationAction, { button: string; title: string; text: string }>;
    declineReasonLabel: string;
    cancelText: string;
    errorText: string;
};

type PerformAction = (advertId: string, action: ApplicationAction, reason?: string) => Promise<void>;

/** The actions the API currently allows, each behind a confirm overlay. */
function ApplicationActions({
    thread,
    texts,
    onPerform,
}: {
    thread: MessageThread;
    texts: ApplicationTexts;
    onPerform: PerformAction;
}) {
    const [confirming, setConfirming] = useState<ApplicationAction | null>(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [reason, setReason] = useState("");
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (confirming && dialog && !dialog.open) dialog.showModal();
    }, [confirming]);

    if (thread.availableActions.length === 0) return null;

    const open = (action: ApplicationAction) => {
        setFailed(false);
        setReason("");
        setConfirming(action);
    };

    const confirm = async (action: ApplicationAction) => {
        setBusy(true);
        setFailed(false);
        try {
            await onPerform(thread.advertId, action, action === "DeclineInterest" ? reason : undefined);
            dialogRef.current?.close();
        } catch (err) {
            console.error("Error performing advert action:", err);
            setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    const current = confirming ? texts.actions[confirming] : null;

    return (
        <div className="mt-3">
            {thread.availableActions.length > 0 && (
                <div className="flex flex-wrap gap-3">
                    {thread.availableActions.map((action) => (
                        <button
                            key={action}
                            type="button"
                            className={
                                action === "CancelOpenHouse" || action === "DeclineInterest"
                                    ? "button-functional-small-secondary button-functional--close"
                                    : "button-functional-small"
                            }
                            onClick={() => open(action)}
                        >
                            {texts.actions[action].button}
                        </button>
                    ))}
                </div>
            )}

            {confirming && current && (
                <dialog
                    ref={dialogRef}
                    onClose={() => setConfirming(null)}
                    onClick={(e) => {
                        if (e.target === dialogRef.current) dialogRef.current?.close();
                    }}
                >
                    <div className="modal-body">
                        <button
                            type="button"
                            className="top-3 right-3 absolute place-items-center grid bg-almost-black rounded-full w-9 h-9 cursor-pointer modal-close"
                            aria-label={texts.cancelText}
                            onClick={() => dialogRef.current?.close()}
                        >
                            <span className="block bg-white w-5 h-5" style="mask: var(--icon-functionClose) center / contain no-repeat;" />
                        </button>

                        <h3 className="mb-4">{current.title}</h3>
                        <p className="mb-6">{current.text}</p>

                        {confirming === "DeclineInterest" && (
                            <>
                                <label htmlFor="aam-decline-reason" className="block mb-2 font-semibold text-dark-text text-sm">
                                    {texts.declineReasonLabel}
                                </label>
                                <textarea
                                    id="aam-decline-reason"
                                    className="mb-6 resize-none"
                                    rows={3}
                                    value={reason}
                                    disabled={busy}
                                    onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
                                />
                            </>
                        )}

                        {failed && (
                            <div className="bg-pka-red/5 mb-4 p-3 border border-pka-red/20 text-pka-red text-sm">
                                {texts.errorText}
                            </div>
                        )}

                        <div className="flex flex-col gap-3">
                            <button type="button" className="w-full button" disabled={busy} onClick={() => confirm(confirming)}>
                                {current.button}
                            </button>
                            <button
                                type="button"
                                className="text-pka-red hover:text-bordeaux-red underline cursor-pointer"
                                onClick={() => dialogRef.current?.close()}
                            >
                                {texts.cancelText}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    );
}

function MessageThreadList({
    threads,
    selectedThreadId,
    onSelectThread,
    statusTexts,
    youText,
    employeeText,
    noResultsText,
}: {
    threads: MessageThread[];
    selectedThreadId: string | null;
    onSelectThread: (id: string | null) => void;
    statusTexts: StatusTexts;
    youText: string;
    employeeText: string;
    noResultsText: string;
}) {
    if (threads.length === 0) {
        return (
            <div className="p-8 text-dark-text/60 text-center">
                <span
                    className="block mx-auto mb-4 w-12 h-12 text-dark-text/30 aam-icon aam-icon-envelope"
                    aria-hidden="true"
                />
                <p>{noResultsText}</p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-medium-light-sand">
            {threads.map((thread) => {
                const status = threadStatus(thread);
                const selected = selectedThreadId === thread.advertId;
                return (
                    <div
                        key={thread.advertId}
                        onClick={() => onSelectThread(thread.advertId)}
                        className={`cursor-pointer border-l-4 p-4 transition-colors hover:bg-light-sand ${
                            selected ? "bg-light-sand" : ""
                        } ${
                            status === "unread"
                                ? "border-l-warning-yellow bg-medium-light-sand/30"
                                : "border-l-transparent"
                        }`}
                    >
                        <div className="flex justify-between items-start gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-dark-text text-sm truncate">
                                    {thread.residenceAddress}
                                </p>
                                <div className="mt-1">
                                    <ApplicationStatusChip thread={thread} texts={statusTexts} />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {thread.unreadCount > 0 && (
                                    <span className="place-items-center grid bg-pka-red rounded-full w-6 h-6 text-white text-xs leading-none">
                                        {thread.unreadCount}
                                    </span>
                                )}
                                <ThreadStatusIcon status={status} />
                            </div>
                        </div>

                        <p
                            className={`line-clamp-2 text-xs ${
                                thread.hasUnread
                                    ? "font-semibold text-dark-text"
                                    : "text-dark-text/70"
                            }`}
                        >
                            {thread.lastMessagePreview
                                ? `${thread.lastMessageIsFromUser ? youText : employeeText}: ${thread.lastMessagePreview}`
                                : ""}
                        </p>

                        <p className="mt-2 text-dark-text/40 text-xs">
                            {formatDanishDateTime(thread.lastMessageDate)}
                        </p>
                    </div>
                );
            })}
        </div>
    );
}

function MessageBubble({
    message,
    youText,
    employeeText,
}: {
    message: TransformedMessage;
    youText: string;
    employeeText: string;
}) {
    const isFromUser = message.isFromUser;
    return (
        <div className={`flex ${isFromUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`aam-bubble p-3 text-sm md:p-4 ${
                    isFromUser
                        ? "bg-pka-red/10 text-dark-text"
                        : "bg-light-sand text-dark-text"
                }`}
            >
                <p className="leading-relaxed whitespace-pre-wrap">
                    {message.text}
                </p>
                <p className="mt-1.5 text-dark-text/40 text-xs">
                    {isFromUser ? youText : employeeText} &middot;{" "}
                    {formatDanishDateTime(message.created)}
                </p>
            </div>
        </div>
    );
}

function MessageDetail({
    thread,
    onBack,
    replyText,
    onReplyChange,
    onSend,
    sending,
    sendError,
    statusTexts,
    noMessagesText,
    replyPlaceholder,
    sendingText,
    sendButtonText,
    youText,
    employeeText,
    backToApplicationsText,
    applicationTexts,
    onPerformAction,
}: {
    thread: MessageThread;
    onBack: () => void;
    replyText: string;
    onReplyChange: (text: string) => void;
    onSend: () => void;
    sending: boolean;
    sendError: string | null;
    statusTexts: StatusTexts;
    noMessagesText: string;
    replyPlaceholder: string;
    sendingText: string;
    sendButtonText: string;
    youText: string;
    employeeText: string;
    backToApplicationsText: string;
    applicationTexts: ApplicationTexts;
    onPerformAction: PerformAction;
}) {
    const messagesRef = useRef<HTMLDivElement>(null);

    // Open on the newest message. Scrolls the message pane only - scrollIntoView
    // also scrolled the page, pushing the heading out of view on arrival.
    useEffect(() => {
        const el = messagesRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [thread.messages]);

    return (
        <>
            <div className="p-4 md:p-6 border-medium-light-sand border-b">
                <button
                    type="button"
                    onClick={onBack}
                    className="md:hidden flex items-center gap-2 mb-4 font-semibold text-pka-red text-sm cursor-pointer"
                >
                    <span
                        className="w-4 h-4 aam-icon aam-icon-back"
                        aria-hidden="true"
                    />
                    {backToApplicationsText}
                </button>

                <a
                    href={`${thread.propertyUrlPath ?? "#"}/${thread.advertId}`}
                    className="font-bold text-dark-text hover:text-pka-red text-lg md:text-xl transition-colors"
                >
                    {thread.residenceAddress}
                </a>
                <div className="mt-2">
                    <ApplicationStatusChip thread={thread} texts={statusTexts} />
                </div>
                <ApplicationActions
                    key={thread.advertId}
                    thread={thread}
                    texts={applicationTexts}
                    onPerform={onPerformAction}
                />
            </div>

            <div ref={messagesRef} className="flex-1 space-y-3 p-4 md:p-6 overflow-y-auto">
                {thread.messages.length === 0 ? (
                    <p className="py-8 text-dark-text/60 text-center">
                        {noMessagesText}
                    </p>
                ) : (
                    thread.messages.map((message) => (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            youText={youText}
                            employeeText={employeeText}
                        />
                    ))
                )}
            </div>

            <div className="p-4 md:p-6 border-medium-light-sand border-t">
                {sendError && (
                    <div className="bg-pka-red/5 mb-4 p-3 border border-pka-red/20 text-pka-red text-sm">
                        {sendError}
                    </div>
                )}
                <div className="flex flex-col gap-3">
                    <label
                        htmlFor="aam-reply"
                        className="font-semibold text-dark-text text-sm"
                    >
                        {sendButtonText}
                    </label>
                    <textarea
                        id="aam-reply"
                        value={replyText}
                        onInput={(e) =>
                            onReplyChange(
                                (e.target as HTMLTextAreaElement).value,
                            )
                        }
                        placeholder={replyPlaceholder}
                        className="resize-none"
                        rows={3}
                        disabled={sending}
                    />
                    <div className="self-end">
                        <button
                            type="button"
                            onClick={onSend}
                            disabled={sending || !replyText.trim()}
                            className="cursor-pointer button"
                        >
                            <span className="inline-flex justify-center items-center gap-2">
                                {sending ? (
                                    <>
                                        <span className="border-white border-b-2 rounded-full w-4 h-4 animate-spin" />
                                        {sendingText}
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className="w-4 h-4 aam-icon aam-icon-send"
                                            aria-hidden="true"
                                        />
                                        {sendButtonText}
                                    </>
                                )}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default function AdvertApplicationMessagesIsland(
    props: AdvertApplicationMessagesProps,
) {
    const noApplicationsText = label(
        props.noApplicationsText,
        "Du har endnu ingen ansøgninger.",
    );
    const loadingText = label(props.loadingText, "Indlæser...");
    const errorText = label(
        props.errorText,
        "Beskederne kunne ikke hentes. Prøv igen senere.",
    );
    const mustBeLoggedInText = label(
        props.mustBeLoggedInText,
        "Du skal være logget ind for at se dine ansøgninger.",
    );
    const noMessagesText = label(
        props.noMessagesText,
        "Der er endnu ingen beskeder for denne ansøgning.",
    );
    const replyPlaceholder = label(props.replyPlaceholder, "Skriv dit svar...");
    const sendingText = label(props.sendingText, "Sender...");
    const sendButtonText = label(props.sendButtonText, "Send");
    const youText = label(props.youText, "Dig");
    const employeeText = label(props.employeeText, "PKA Ejendomme");
    const backToApplicationsText = label(
        props.backToApplicationsText,
        "Tilbage til ansøgninger",
    );
    const searchPlaceholder = label(
        props.searchPlaceholder,
        "Søg i beskeder...",
    );
    const noResultsText = label(props.noResultsText, "Ingen beskeder fundet");
    const selectThreadText = label(
        props.selectThreadText,
        "Vælg en besked for at læse og svare",
    );

    const statusTexts: StatusTexts = {
        status: props.applicationStatusTexts ?? {},
        openHouse: props.openHouseText ?? "",
    };

    const applicationTexts: ApplicationTexts = {
        actions: {
            JoinOpenHouse: {
                button: props.joinOpenHouseButtonText ?? "",
                title: props.joinOpenHouseConfirmTitle ?? "",
                text: props.joinOpenHouseConfirmText ?? "",
            },
            CancelOpenHouse: {
                button: props.cancelOpenHouseButtonText ?? "",
                title: props.cancelOpenHouseConfirmTitle ?? "",
                text: props.cancelOpenHouseConfirmText ?? "",
            },
            ConfirmInterest: {
                button: props.confirmInterestButtonText ?? "",
                title: props.confirmInterestConfirmTitle ?? "",
                text: props.confirmInterestConfirmText ?? "",
            },
            DeclineInterest: {
                button: props.declineInterestButtonText ?? "",
                title: props.declineInterestConfirmTitle ?? "",
                text: props.declineInterestConfirmText ?? "",
            },
        },
        declineReasonLabel: props.declineReasonLabel ?? "",
        cancelText: props.actionCancelText ?? "",
        errorText: props.actionErrorText ?? "",
    };

    const {
        threads,
        loading,
        error,
        isLoggedIn,
        selectedThreadId,
        setSelectedThreadId,
        getThreadById,
        sendMessage,
        sending,
        sendError,
        markMessageAsRead,
        performAction,
    } = useAdvertApplicationMessages();

    const [replyText, setReplyText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<ThreadStatus | "all">(
        "all",
    );

    const statusFilters: { value: ThreadStatus | "all"; text: string }[] = [
        { value: "all", text: label(props.filterAllText, "Alle") },
        { value: "unread", text: label(props.filterUnreadText, "Ulæste") },
        { value: "read", text: label(props.filterReadText, "Læste") },
        { value: "replied", text: label(props.filterRepliedText, "Besvarede") },
    ];
    const activeFilter =
        statusFilters.find((option) => option.value === statusFilter) ??
        statusFilters[0];

    const filterRef = useFormControl<HTMLDivElement>(initDropdown);

    const selectedThread = selectedThreadId
        ? (getThreadById(selectedThreadId) ?? null)
        : null;

    // Everything the applicant has is already in memory - one `me` query loads
    // the lot - so search and status filter both run here, no refetch.
    const filteredThreads = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return threads.filter((thread) => {
            if (statusFilter !== "all" && threadStatus(thread) !== statusFilter)
                return false;
            if (!query) return true;
            return (
                thread.residenceAddress.toLowerCase().includes(query) ||
                thread.messages.some((message) =>
                    message.text.toLowerCase().includes(query),
                )
            );
        });
    }, [threads, searchQuery, statusFilter]);

    // Mark the open thread's unread employee messages as read. The hook holds
    // the 2s timer and clears it when the thread changes.
    useEffect(() => {
        if (!selectedThread) return;
        selectedThread.messages
            .filter(
                (message) =>
                    message.isFromEmployee &&
                    !message.isFromUser &&
                    message.isUnread,
            )
            .forEach((message) => markMessageAsRead(message.id));
    }, [selectedThread, markMessageAsRead]);

    useEffect(() => {
        setReplyText("");
    }, [selectedThreadId]);

    const handleSendMessage = async () => {
        if (!selectedThreadId || !replyText.trim()) return;
        const success = await sendMessage(selectedThreadId, replyText);
        if (success) setReplyText("");
    };

    const heading = (
        <h1 className="advert-application-messages-title">
            {props.title}
        </h1>
    );

    if (!isLoggedIn) {
        return (
            <div className="w-full">
                {heading}
                <div className="bg-light-sand p-8 text-center text-dark-sand">
                    <p>{mustBeLoggedInText}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="w-full">
                {heading}
                <div className="flex justify-center items-center py-12">
                    <span className="border-pka-red border-b-2 rounded-full w-8 h-8 animate-spin" />
                    <span className="ml-3 text-dark-sand">{loadingText}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full">
                {heading}
                <div className="bg-pka-red/5 p-4 border border-pka-red/20 text-pka-red">
                    {error || errorText}
                </div>
            </div>
        );
    }

    if (threads.length === 0) {
        return (
            <div className="w-full">
                {heading}
                <div className="bg-light-sand p-8 text-dark-sand text-center">
                    <p>{noApplicationsText}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {heading}

            {/* On mobile the open thread takes the whole component, so the
                controls step aside with the list. */}
            <div
                className={`mb-6 flex-col gap-4 md:flex-row ${
                    selectedThread ? "hidden md:flex" : "flex"
                }`}
            >
                <div className="relative flex-1">
                    {/* 24px to match the nav's search button. The input must
                        NOT carry .dropdown-trigger: that is a dropdown class
                        whose only effect here is position:relative, which paints
                        the input's white background over this icon. */}
                    <span
                        className="top-1/2 left-4 absolute w-6 h-6 text-dark-text -translate-y-1/2 aam-icon aam-icon-search"
                        aria-hidden="true"
                    />
                    <input
                        type="search"
                        className="pl-12"
                        value={searchQuery}
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                        onInput={(e) =>
                            setSearchQuery((e.target as HTMLInputElement).value)
                        }
                    />
                </div>
                {/* The ref goes on a wrapper, not on .dropdown itself:
                    initDropdown scans its scope's descendants, so a ref on the
                    root would leave the control unwired. It owns open/close,
                    keyboard roving and outside-click; the options just report
                    the pick back through onClick. */}
                <div ref={filterRef} className="w-full md:w-56">
                    <div className="dropdown">
                        <button type="button" className="dropdown-trigger">
                            {activeFilter.text}
                        </button>
                        <div className="dropdown-panel" role="listbox">
                            {statusFilters.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className="dropdown-option"
                                    role="option"
                                    aria-selected={statusFilter === option.value}
                                    data-value={option.value}
                                    onClick={() => setStatusFilter(option.value)}
                                >
                                    {option.text}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 bg-white shadow-sm border border-medium-light-sand overflow-hidden aam-panel">
                <div
                    className={`overflow-y-auto border-medium-light-sand md:col-span-2 md:border-r ${
                        selectedThread ? "hidden md:block" : "block"
                    }`}
                >
                    <MessageThreadList
                        threads={filteredThreads}
                        selectedThreadId={selectedThreadId}
                        onSelectThread={setSelectedThreadId}
                        statusTexts={statusTexts}
                        youText={youText}
                        employeeText={employeeText}
                        noResultsText={noResultsText}
                    />
                </div>

                <div
                    className={`min-h-0 flex-col md:col-span-3 ${
                        selectedThread ? "flex" : "hidden md:flex"
                    }`}
                >
                    {selectedThread ? (
                        <MessageDetail
                            thread={selectedThread}
                            onBack={() => setSelectedThreadId(null)}
                            replyText={replyText}
                            onReplyChange={setReplyText}
                            onSend={handleSendMessage}
                            sending={sending}
                            sendError={sendError}
                            statusTexts={statusTexts}
                            noMessagesText={noMessagesText}
                            replyPlaceholder={replyPlaceholder}
                            sendingText={sendingText}
                            sendButtonText={sendButtonText}
                            youText={youText}
                            employeeText={employeeText}
                            backToApplicationsText={backToApplicationsText}
                            applicationTexts={applicationTexts}
                            onPerformAction={performAction}
                        />
                    ) : (
                        <div className="flex flex-1 justify-center items-center bg-light-sand/30 p-8 text-dark-text/50 text-center">
                            {selectThreadText}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
